<template>
  <div class="card main-chart p-3" :class="{ 'main-chart--embedded': injectedReportData }">
    <div class="d-flex justify-content-between align-items-center mb-3" v-if="!hideControls">
      <div></div>
      <DateRange compact />
    </div>
    <!-- Independent of hideControls: the standalone report-view page hides this toolbar's own
         date picker (it has its own PeriodGranularitySelector) but still needs the warning.
         Only real exclusion is dashboard widgets, which inject a local, isolated report state
         and always have their dates overridden by the dashboard's global period anyway. -->
    <HardcodedDateWarning v-if="!injectedReportData" />

    <div v-if="loading" class="d-flex justify-content-center h-100 align-items-center" style="min-height: 300px;">
      <div class="spinner-border" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="allSeriesData && allSeriesData.length > 0">
      <!-- Funnel Chart -->
      <div style="height: 350px;">
        <Bar
          :options="chartOptions"
          :data="chartJsData"
          :plugins="chartPlugins"
        />
      </div>

      <!-- Funnel Steps Summary (Baseline) -->
      <div class="funnel-summary-container mt-4 pt-3" v-if="!hideStepsData">
        <div
          class="funnel-summary-step"
          v-for="(step, index) in baselineSteps"
          :key="index"
        >
          <div class="step-header d-block">
            <div class="d-flex align-items-center mb-1">
              <span class="step-badge me-2">{{ index + 1 }}</span>
              <span class="step-title text-truncate">
                 {{ $t('ReportBuilder.funnels.completed') }} {{ index + 1 }} {{ (index + 1) === 1 ? $t('ReportBuilder.funnels.step') : $t('ReportBuilder.funnels.steps') }}<template v-if="getCustomStepLabel(index)">: {{ getCustomStepLabel(index) }}</template>
              </span>
            </div>
            <div v-if="getEventNameBadge(index)" class="ms-1">
              <span class="badge bg-light text-dark border opacity-75 fw-normal" style="font-size: 11px;">{{ getEventNameBadge(index) }}</span>
            </div>
          </div>

          <div class="step-stats mt-3">
            <!-- First step summary -->
            <div class="stat-line converted" v-if="index === 0">
              <i class="fas fa-arrow-right"></i>
              <span
                class="step-users-link"
                @click="openStepUsers(index + 1)"
              >
                {{ step.converted }} {{ $t('ReportBuilder.funnels.persons') }}
              </span>
            </div>

            <!-- Subsequent steps -->
            <template v-else>
              <div class="stat-line converted">
                <i class="fas fa-arrow-right"></i>
                <span
                  class="step-users-link"
                  @click="openStepUsers(index + 1)"
                >
                  {{ step.converted }} {{ $t('ReportBuilder.funnels.persons') }}
                  <span v-if="reportData.source_request.funnel_conversion_mode === 'relative'">({{ step.conversionFromPrevRate.toFixed(2) }}%)</span>
                  <span v-else>({{ step.conversionSoFarRate.toFixed(2) }}%)</span>
                </span>
              </div>

              <div class="stat-line dropped">
                <i class="fas fa-arrow-down" style="transform: rotate(45deg);"></i>
                <span>
                  {{ step.dropped }} {{ $t('ReportBuilder.funnels.persons') }}
                  <span v-if="reportData.source_request.funnel_conversion_mode === 'relative'">({{ step.dropoffFromPrevRate.toFixed(2) }}%)</span>
                  <span v-else>({{ (step.dropped / baselineSteps[0].converted * 100).toFixed(2) }}%)</span>
                </span>
              </div>
            </template>
          </div>
        </div>
      </div>

      <!-- Detailed Results Table -->
      <div class="detailed-results mt-5" v-if="!hideDetailedData">
        <h5 class="detailed-results-title mb-4" :class="{ 'is-compact': injectedReportData }">{{ $t('ReportBuilder.funnels.detailedResults') || 'Detailed results' }}</h5>

        <div class="table-responsive">
          <table class="table detailed-table border-radius-md overflow-hidden">
            <thead>
              <tr class="header-group">
                <th rowspan="2" class="breakdown-col-header"></th>
                <!-- Color: constructor only — read-only views show the color as a left
                     border on the breakdown cell instead, no column needed. -->
                <th v-if="editable" rowspan="2" class="text-center group-header-cell" style="width: 80px;">{{ $t('ReportBuilder.table.color') || 'COLOR' }}</th>
                <th rowspan="2" class="text-center group-header-cell">{{ $t('ReportBuilder.funnels.totalConversion') || 'TOTAL CONVERSION' }}</th>
                
                <th v-for="(step, i) in reportData.source_request.requests" :key="'h-'+i"
                    :colspan="i === 0 ? 1 : 7"
                    class="step-header-cell text-start"
                >
                  <div class="d-flex align-items-center">
                    <span class="step-badge-mini me-2">{{ i + 1 }}</span>
                    {{ getStepLabel(i).toUpperCase() }}
                  </div>
                </th>
              </tr>
              <tr class="header-cols">
                <!-- Step 1 columns -->
                <th class="text-center ps-1">{{ $t('ReportBuilder.funnels.entered') || 'ENTERED' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>

                <!-- Steps 2+ columns -->
                <template v-for="(step, i) in reportData.source_request.requests.slice(1)" :key="'header-step-' + (i + 1)">
                  <th class="text-center th-converted">{{ $t('ReportBuilder.funnels.converted') || 'CONVERTED' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="text-center th-dropped">{{ $t('ReportBuilder.funnels.droppedOff') || 'DROPPED OFF' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="text-center th-conv-sofar">{{ $t('ReportBuilder.funnels.conversionSoFar') || 'CONVERSION SO FAR' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="text-center th-conv-prev">{{ $t('ReportBuilder.funnels.conversionFromPrevious') || 'CONVERSION FROM PREVIOUS' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="text-center">{{ $t('ReportBuilder.funnels.medianTime') || 'MEDIAN TIME' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="text-center">{{ $t('ReportBuilder.funnels.averageTime') || 'AVERAGE TIME' }} <i class="fas fa-sort-amount-down-alt ms-1 opacity-50"></i></th>
                  <th class="border-end-0"></th> <!-- Padding/spacer -->
                </template>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="series in allSeriesData"
                :key="series.key || series.name"
                :class="{'baseline-row fw-bold': series.isBaseline}"
              >
                <!-- Breakdown Row Content -->
                <td
                  class="breakdown-cell d-flex align-items-center"
                  :style="[
                    isUtmMode && !series.isBaseline ? { paddingLeft: (12 + (series._depth || 0) * 20) + 'px' } : {},
                    !series.isBaseline ? { borderLeft: '2px solid ' + getColor(series.dataIndex) } : { borderLeft: '2px solid transparent' }
                  ]"
                >
                  <!-- UTM drill-down toggle (UTM mode only) -->
                  <template v-if="isUtmMode && !series.isBaseline">
                    <span
                      v-if="hasUtmChildren(series.utmRow)"
                      class="btn-utm-expand"
                      @click="toggleUtmExpand(series.utmRow)"
                    >
                      <span
                        v-if="isUtmLoading(series.utmRow)"
                        class="spinner-grow spinner-grow-sm text-secondary"
                        role="status"
                        style="width: 12px; height: 12px;"
                      ></span>
                      <template v-else>{{ isUtmExpanded(series.utmRow) ? '−' : '+' }}</template>
                    </span>
                    <span v-else class="utm-leaf-spacer-inline"></span>
                  </template>
                  <el-checkbox-button
                    v-if="!series.isBaseline"
                    :model-value="!hiddenDatasets.includes(series.name)"
                    size="small"
                    class="funnel-visibility-btn me-2"
                    @change="toggleVisibility(series.name)"
                  >
                    <i class="fas fa-check"></i>
                  </el-checkbox-button>
                  <span class="breakdown-name">{{ series.name }}</span>
                </td>
                
                <!-- Color Indicator: constructor only, see header comment above -->
                <td v-if="editable" class="text-center">
                  <el-popover
                    v-if="!series.isBaseline && !hasGroupings"
                    placement="bottom"
                    :width="200"
                    trigger="click"
                    popper-class="rb-color-popover"
                  >
                    <template #reference>
                      <div 
                        class="series-color-indicator cursor-pointer mx-auto" 
                        :style="{ backgroundColor: getColor(series.dataIndex) }"
                      ></div>
                    </template>
                    
                    <div class="color-palette-grid">
                      <div 
                        v-for="color in palette" 
                        :key="color"
                        class="color-option"
                        :class="{ active: getColor(series.dataIndex) === color }"
                        :style="{ backgroundColor: color }"
                        @click="updateColor(0, color)"
                      ></div>
                    </div>
                  </el-popover>
                  <div 
                    v-else
                    class="series-color-indicator mx-auto" 
                    :style="{ backgroundColor: series.isBaseline ? '#2dd4bf' : getColor(series.dataIndex) }"
                    :class="{ 'baseline-indicator': series.isBaseline }"
                  ></div>
                </td>

                <td class="text-center">{{ series.steps[series.steps.length-1].conversionSoFarRate.toFixed(2) }}%</td>

                <!-- Step 1 data -->
                <td class="text-center step-header-cell">
                  <span class="step-users-link" @click="openStepUsers(1)">{{ series.steps[0].converted }}</span>
                </td>

                <!-- Steps 2+ data -->
                <template v-for="(step, i) in series.steps.slice(1)" :key="'d-'+series.name+'-'+i">
                  <td class="text-center cell-converted step-header-cell">
                    <span class="step-users-link" @click="openStepUsers(i + 2)">{{ step.converted }}</span>
                  </td>
                  <td class="text-center cell-dropped">{{ step.dropped }}</td>
                  <td class="text-center cell-conv-sofar">{{ step.conversionSoFarRate.toFixed(2) }}%</td>
                  <td class="text-center cell-conv-prev">{{ step.conversionFromPrevRate.toFixed(2) }}%</td>
                  <td class="text-center">{{ formatDuration(step.medianTime) }}</td>
                  <td class="text-center">{{ formatDuration(step.averageTime) }}</td>
                  <td class="border-end-0"></td>
                </template>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-else class="d-flex justify-content-center h-100 align-items-center text-muted" style="min-height: 300px;">
      {{ $t('General.NoData') }}
    </div>
  </div>

  <!-- Step Users Dialog -->
  <UserListDialog
    v-model:visible="stepUsersDialog.visible"
    :title="stepUsersDialogTitle"
    :users="stepUsersDialog.users"
    :total="stepUsersDialog.total"
    :loading="stepUsersDialog.loading"
    :page="stepUsersDialog.page"
    :page-size="stepUsersDialog.pageSize"
    @update:page="stepUsersDialog.page = $event; loadStepUsers()"
  >
    <template #extra-th>
      <th>{{ $t('UserListDialog.enteredAt') }}</th>
    </template>
    <template #extra-td="{ row }">
      <td style="font-size: 12px; white-space: nowrap;">{{ row.step_time || '—' }}</td>
    </template>
  </UserListDialog>
</template>

<script>
import DateRange from "@/views/report_builder/builder/_dateRange.vue"
import HardcodedDateWarning from "@/views/report_builder/builder/_hardcodedDateWarning.vue"
import UserListDialog from "@/views/components/graspil/UserListDialog.vue"
import { useEvents } from "@/composables/useEvents";
import { Bar } from 'vue-chartjs'
import 'chart.js/auto'
import TimeHelper from "@/helpers/TimeHelper"
import ReportBuilderService from "@/helpers/api/reportBuilder.service"

export default {
  name: "FunnelsChart",
  components: { DateRange, HardcodedDateWarning, Bar, UserListDialog },
  inject: {
    injectedReportData: {
      from: 'injectedReportData',
      default: null
    },
    // In isolated mode (dashboard widget) ad-hoc requests must be built from the
    // owning Report.vue config, not from the global builder store.
    injectedBuildPayload: {
      from: 'injectedBuildPayload',
      default: null
    },
    // Color picker column only makes sense in the constructor — everywhere else the
    // table is read-only, the left border on the breakdown cell already carries the
    // color indication (see :style on .breakdown-cell below).
    editable: {
      from: 'injectedEditable',
      default: false
    },
    injectedExtraParams: {
      from: 'injectedExtraParams',
      default: null
    }
  },
  setup() {
    const { getEventName: resolveEventName } = useEvents();
    return { resolveEventName };
  },
  props: {
    hideControls: {
      type: Boolean,
      default: false
    },
    hideDetailedData: {
      type: Boolean,
      default: false
    },
    hideStepsData: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      // UTM breakdown drill-down state (parallel to _utm_report.vue)
      utmCache: {},
      utmExpandedKeys: {},
      utmLoadingKeys: {},
      palette: [
        '#409EFF', '#67C23A', '#E6A23C', '#F56C6C', '#909399',
        '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
        '#FF9F40', '#8E44AD', '#2ECC71', '#F1C40F', '#E74C3C',
        '#1ABC9C', '#3498DB', '#9B59B6', '#34495E', '#7F8C8D'
      ],
      stepUsersDialog: {
        visible: false,
        loading: false,
        step: 1,
        users: [],
        total: 0,
        page: 1,
        pageSize: 50,
      }
    }
  },
  watch: {
    // Reset drill-down cache whenever the underlying report reloads.
    'reportData.chartData'() {
      this.utmCache = {}
      this.utmExpandedKeys = {}
      this.utmLoadingKeys = {}
    }
  },
  computed: {
    reportData() {
      return this.injectedReportData || this.$store.state.ReportFromBuilder
    },
    chartData() {
      return this.reportData.chartData || []
    },
    hiddenDatasets() {
      return this.reportData.hiddenDatasets || []
    },
    hasGroupings() {
      if (!this.chartData || !this.chartData.length) return false
      if (this.isUtmMode) return true
      return this.chartData.some(dataset => dataset.separators && dataset.separators.length > 0)
    },
    isUtmMode() {
      return !!this.reportData.source_request?.funnel_utm_breakdown
    },
    /**
     * Flattened row list for UTM breakdown — parent rows plus any
     * cached children of expanded parents, each row tagged with _depth.
     * Mirrors _utm_report.vue::buildFlatList.
     */
    utmFlatRows() {
      if (!this.isUtmMode) return []
      return this.buildUtmFlatList(this.chartData, 0)
    },
    allSeriesData() {
      if (!this.chartData || !this.chartData.length) return [];

      const isUtm = this.isUtmMode;
      // Rows used to render table body: flat UTM tree in UTM mode,
      // raw chartData otherwise. Baseline is always computed from the
      // top-level chartData — children are subdivisions of their parent,
      // so summing them in would double-count.
      const displayRows = isUtm
        ? this.utmFlatRows
        : [...this.chartData].sort((a, b) => (b.step_1 || 0) - (a.step_1 || 0));
      const baselineRows = this.chartData;
      const numSteps = this.reportData.source_request.requests.length;
      const series = [];

      // Only calculate and add a separate Baseline row if we have multiple breakdown results
      if (baselineRows.length > 1) {
        const totalRow = {
          name: 'Baseline',
          key: '__baseline__',
          isBaseline: true,
          steps: []
        };

        for (let i = 1; i <= numSteps; i++) {
          let person_count = 0;
          let totalAvgTime = 0;
          let avgTimeCount = 0;
          let totalMedianTime = 0;
          let medianTimeCount = 0;

          baselineRows.forEach(r => {
            person_count += (r[`step_${i}`] || 0);
            if (r[`step_${i}_average_conversion_time`]) {
              totalAvgTime += r[`step_${i}_average_conversion_time`];
              avgTimeCount++;
            }
            if (r[`step_${i}_median_conversion_time`]) {
              totalMedianTime += r[`step_${i}_median_conversion_time`];
              medianTimeCount++;
            }
          });

          totalRow.steps.push({
            converted: person_count,
            averageTime: avgTimeCount > 0 ? totalAvgTime / avgTimeCount : null,
            medianTime: medianTimeCount > 0 ? totalMedianTime / medianTimeCount : null
          });
        }
        this.calculateRatesForSeries(totalRow.steps);
        series.push(totalRow);
      }

      // Add Breakdown rows
      displayRows.forEach((r, rIndex) => {
        const name = isUtm
          ? (r.utm_name || this.$t('ReportBuilder.utm.none') || '(none)')
          : ((r.separators && r.separators.length > 0) ? r.separators.join(', ') : (this.$t('ReportBuilder.funnels.total') || 'Total'));
        const s = {
          name,
          key: isUtm ? this.utmRowKey(r) + '_' + rIndex : name,
          isBaseline: false,
          dataIndex: rIndex,
          _depth: isUtm ? (r._depth || 0) : 0,
          // Keep a reference to the raw UTM row so the template can
          // read next_level / current_level for the expand toggle.
          utmRow: isUtm ? r : null,
          steps: []
        };

        // Populate steps for this breakdown
        this.reportData.source_request.requests.forEach((req, i) => {
          const stepPrefix = `step_${i + 1}`;
          s.steps.push({
            converted: r[stepPrefix] || 0,
            medianTime: r[`${stepPrefix}_median_conversion_time`] || 0,
            averageTime: r[`${stepPrefix}_average_conversion_time`] || 0
          });
        });

        this.calculateRatesForSeries(s.steps);
        series.push(s);
      });

      return series;
    },
    stepUsersDialogTitle() {
      const stepNum = this.stepUsersDialog.step
      const label   = this.getStepLabel(stepNum - 1)
      return `${this.$t('ReportBuilder.funnels.stepUsers.dialogTitle')} ${stepNum}: ${label}`
    },
    baselineSteps() {
      if (!this.allSeriesData || this.allSeriesData.length === 0) return [];
      const baseline = this.allSeriesData.find(s => s.isBaseline);
      if (baseline) return baseline.steps;
      
      // If no baseline found but we have data, use the first series (usually "Total" if no breakdown)
      return this.allSeriesData[0].steps;
    },
    chartJsData() {
      const labels = this.reportData.source_request.requests.map((_, i) => {
        return `${i + 1}. ${this.getStepLabel(i)}`;
      });
      
      const datasets = [];
      // In UTM mode only show top-level (_depth === 0) rows on the bar chart;
      // expanded children would clutter it without adding insight.
      const breakdownSeries = this.allSeriesData.filter(s => {
        if (s.isBaseline) return false;
        return true;
      });

      breakdownSeries.forEach((s) => {
        datasets.push({
          label: s.name,
          data: s.steps.map(step => this.reportData.source_request.funnel_conversion_mode === 'relative' ? step.conversionFromPrevRate : step.conversionSoFarRate),
          backgroundColor: this.getColor(s.dataIndex),
          borderWidth: 0,
          barPercentage: 0.8,
          categoryPercentage: 0.8,
          borderRadius: 6,
          hidden: this.hiddenDatasets.includes(s.name)
        });
      });

      return {
        labels: labels,
        datasets: datasets
      }
    },
    chartOptions() {
      const vm = this;
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            grid: {
              display: true,
              drawOnChartArea: true,
              drawTicks: false,
              color: '#f3f4f6',
              lineWidth: 1,
              offset: true
            },
            ticks: {
                font: {
                    size: 11,
                    weight: 'bold'
                }
            }
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              callback: function(value) {
                return value + '%';
              },
              stepSize: 20
            },
            grid: {
              borderDash: [5, 5],
              drawBorder: false,
            }
          }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              title: function(context) {
                const index = context[0].dataIndex;
                const seriesName = context[0].dataset.label;
                const stepLabel = vm.getStepLabel(index);
                return `${seriesName} • Step ${index + 1} (${stepLabel})`;
              },
              label: function(context) {
                const index = context.dataIndex;
                const sName = context.dataset.label;
                const series = vm.allSeriesData.find(s => s.name === sName);
                if (!series) return null;
                
                const stats = series.steps[index];
                const lines = [
                  `${vm.$t('ReportBuilder.funnels.converted')}: ${stats.converted}`,
                  `${vm.$t('ReportBuilder.funnels.conversionSoFar')}: ${stats.conversionSoFarRate.toFixed(2)}%`,
                ];
                
                if (index > 0) {
                    lines.push(`${vm.$t('ReportBuilder.funnels.droppedOff')}: ${stats.dropped}`);
                    lines.push(`${vm.$t('ReportBuilder.funnels.conversionFromPrevious')}: ${stats.conversionFromPrevRate.toFixed(2)}%`);
                    if (stats.averageTime) lines.push(`${vm.$t('ReportBuilder.funnels.averageTime') || 'Average time'}: ${vm.formatDuration(stats.averageTime)}`);
                }
                
                return lines;
              }
            },
            backgroundColor: 'rgba(255, 255, 255, 0.98)',
            titleColor: '#111827',
            bodyColor: '#374151',
            borderColor: '#e5e7eb',
            borderWidth: 1,
            padding: 12,
            boxPadding: 6,
            titleFont: { size: 14, weight: 'bold' },
            bodyFont: { size: 13 }
          }
        }
      }
    },
    chartPlugins() {
      return [{
        id: 'funnelBackground',
        beforeDatasetsDraw(chart) {
          const { ctx, scales: { y } } = chart;
          
          // Create stripe pattern
          const stripeCanvas = document.createElement('canvas');
          const size = 12;
          stripeCanvas.width = size;
          stripeCanvas.height = size;
          const stripeCtx = stripeCanvas.getContext('2d');
          stripeCtx.strokeStyle = 'rgba(59, 130, 246, 0.2)'; 
          stripeCtx.lineWidth = 2.5;
          stripeCtx.beginPath();
          stripeCtx.moveTo(0, size);
          stripeCtx.lineTo(size, 0);
          stripeCtx.stroke();
          const pattern = ctx.createPattern(stripeCanvas, 'repeat');

          const top100 = y.getPixelForValue(100);

          chart.data.datasets.forEach((dataset, datasetIndex) => {
            const meta = chart.getDatasetMeta(datasetIndex);
            if (meta.hidden) return;

            meta.data.forEach((bar) => {
              ctx.save();
              ctx.fillStyle = pattern;
              // Draw from bar top to 100% top
              const barTop = bar.y;
              // const barHeight = bar.base - bar.y; // This is the bar itself
              
              // We want to fill the "empty" space above the bar
              // bar.base is 0% (bottom), bar.y is the current value (top)
              // top100 is the 100% line
              ctx.fillRect(bar.x - bar.width / 2, top100, bar.width, barTop - top100);
              ctx.restore();
            });
          });
        }
      }]
    }
  },
  methods: {
    getStepLabel(index) {
      const request = this.reportData.source_request.requests[index];
      if (request) {
        if (request.label) return String(request.label);
        if (request.event_name) return String(request.event_name);
        if (request.event_id === 'all' || request.event_id === null) {
          return this.$t("Components.EventSelector.AnyEvent");
        }
        return String(this.resolveEventName(this.resolveTemplateEventId(request.event_id)) ?? `Step ${index + 1}`);
      }
      return `Step ${index + 1}`;
    },
    // See _chart.vue's resolveTemplateEventId — same placeholder-to-real-id resolution.
    resolveTemplateEventId(eventId) {
      if (typeof eventId !== 'string' || !eventId.startsWith(':')) return eventId;
      const params = this.injectedExtraParams?.value;
      const resolved = params?.[eventId];
      if (resolved === undefined || resolved === null) return eventId;
      if (Array.isArray(resolved)) return resolved.length === 1 ? resolved[0] : (resolved.length ? resolved : eventId);
      return resolved;
    },
    calculateRatesForSeries(steps) {
      const maxCount = steps.length > 0 ? steps[0].converted : 0;
      
      steps.forEach((step, index) => {
        const prevCount = index > 0 ? steps[index - 1].converted : maxCount;
        
        step.dropped = index === 0 ? 0 : prevCount - step.converted;
        step.conversionSoFarRate = maxCount > 0 ? (step.converted / maxCount) * 100 : 0;
        step.conversionFromPrevRate = prevCount > 0 ? (step.converted / prevCount) * 100 : (index === 0 ? 100 : 0);
        step.dropoffFromPrevRate = prevCount > 0 ? (step.dropped / prevCount) * 100 : 0;
      });
    },
    formatDuration(seconds) {
      if (seconds === null || seconds === undefined) return '-';
      return TimeHelper.formatTimeFromSeconds(Math.round(seconds));
    },
    getColor(index) {
      if (this.hasGroupings) {
        return this.palette[index % this.palette.length];
      }
      const request = this.reportData.source_request.requests[0];
      return request?.color || this.palette[index % this.palette.length];
    },
    updateColor(index, color) {
      this.$store.commit('ReportFromBuilder/updateRequest', { 
        index: index, 
        patch: { color: color } 
      });
    },
    getCustomStepLabel(index) {
      const request = this.reportData.source_request.requests[index];
      return request && request.label ? String(request.label) : null;
    },
    getEventNameBadge(index) {
      const request = this.reportData.source_request.requests[index];
      if (request) {
        if (request.event_name) return request.event_name;
        if (request.event_id === 'all' || request.event_id === null) {
          return this.$t("Components.EventSelector.AnyEvent");
        }
        return this.resolveEventName(this.resolveTemplateEventId(request.event_id));
      }
      return null;
    },
    toggleVisibility(name) {
      this.$store.commit('ReportFromBuilder/toggleDatasetVisibility', name);
    },

    // -----------------------------------------------------------------
    // Step users popup
    // -----------------------------------------------------------------
    openStepUsers(step) {
      this.stepUsersDialog.step     = step
      this.stepUsersDialog.page     = 1
      this.stepUsersDialog.users    = []
      this.stepUsersDialog.total    = 0
      this.stepUsersDialog.visible  = true
      this.loadStepUsers()
    },
    async loadStepUsers() {
      this.stepUsersDialog.loading = true
      try {
        const payload = this.injectedBuildPayload
          ? this.injectedBuildPayload()
          : JSON.parse(JSON.stringify(this.$store.getters['ReportFromBuilder/reportConfig']))

        if (payload.settings?.params?.[':resource_key']) {
          const rKeys = payload.settings.params[':resource_key']
          if (Array.isArray(rKeys)) {
            const currentBotKey = this.$store.state.init?.current_bot?.key
            payload.settings.params[':resource_key'] = rKeys
              .map(k => (k === '$current_resource' ? currentBotKey : k))
              .filter(Boolean)
          }
        }

        payload.step      = this.stepUsersDialog.step
        payload.page      = this.stepUsersDialog.page
        payload.page_size = this.stepUsersDialog.pageSize

        const res = await ReportBuilderService.getFunnelStepUsers({}, payload)
        this.stepUsersDialog.users = res?.users ?? res?.data?.users ?? []
        this.stepUsersDialog.total = res?.total ?? res?.data?.total ?? 0
      } catch (e) {
        console.error('Funnel step users error:', e)
      } finally {
        this.stepUsersDialog.loading = false
      }
    },

    // -----------------------------------------------------------------
    // UTM breakdown drill-down
    //
    // Mirrors _utm_report.vue::toggleExpand — on click, fetch children
    // of the clicked row by sending a drill_path payload and cache the
    // result. buildUtmFlatList() then splices children into the display
    // list beneath their parent.
    // -----------------------------------------------------------------
    utmRowKey(row) {
      if (Array.isArray(row.current_level)) {
        return row.current_level.join('_')
      }
      return (row.utm_id ?? '') + '_' + (row.utm_name ?? '')
    },
    buildUtmFlatList(rows, depth) {
      const result = []
      if (!rows) return result
      const sorted = [...rows].sort((a, b) => (b.step_1 || 0) - (a.step_1 || 0))
      for (const row of sorted) {
        result.push({ ...row, _depth: depth })
        const key = this.utmRowKey(row)
        if (this.utmExpandedKeys[key] && this.utmCache[key]) {
          result.push(...this.buildUtmFlatList(this.utmCache[key], depth + 1))
        }
      }
      return result
    },
    isUtmExpanded(row) {
      return !!this.utmExpandedKeys[this.utmRowKey(row)]
    },
    isUtmLoading(row) {
      return !!this.utmLoadingKeys[this.utmRowKey(row)]
    },
    hasUtmChildren(row) {
      return row && row.next_level !== 0 && row.next_level !== '0' && row.next_level != null
    },
    async toggleUtmExpand(row) {
      if (!row) return
      const key = this.utmRowKey(row)

      // Collapse
      if (this.utmExpandedKeys[key]) {
        const next = { ...this.utmExpandedKeys }
        delete next[key]
        this.utmExpandedKeys = next
        return
      }

      // Already cached → just re-expand
      if (this.utmCache[key]) {
        this.utmExpandedKeys = { ...this.utmExpandedKeys, [key]: true }
        return
      }

      this.utmLoadingKeys = { ...this.utmLoadingKeys, [key]: true }

      try {
        const payload = this.injectedBuildPayload
          ? this.injectedBuildPayload()
          : JSON.parse(JSON.stringify(this.$store.getters['ReportFromBuilder/reportConfig']))

        if (!payload.source_request) payload.source_request = {}

        payload.source_request.drill_path = Array.isArray(row.current_level)
          ? [...row.current_level]
          : row.current_level

        // Resource-key placeholder substitution (same as _utm_report.vue)
        if (payload.settings?.params?.[':resource_key']) {
          const rKeys = payload.settings.params[':resource_key']
          if (Array.isArray(rKeys)) {
            const currentBotKey = this.$store.state.init?.current_bot?.key
            payload.settings.params[':resource_key'] = rKeys
              .map(k => (k === '$current_resource' ? currentBotKey : k))
              .filter(Boolean)
          }
        }

        const data = await ReportBuilderService.useReportTemplate({}, payload)
        const children = Array.isArray(data) ? data : (data?.data || [])

        this.utmCache = { ...this.utmCache, [key]: children }
        this.utmExpandedKeys = { ...this.utmExpandedKeys, [key]: true }
      } catch (e) {
        console.error('Funnel UTM drill-down error:', e)
      } finally {
        const next = { ...this.utmLoadingKeys }
        delete next[key]
        this.utmLoadingKeys = next
      }
    }
  }
}
</script>

<style scoped>
/* Embedded in a dashboard widget: drop the nested card chrome — the widget itself
   already draws the surrounding border, see _chart.vue's identical treatment. */
.main-chart--embedded {
  border: none;
  border-radius: 0;
  box-shadow: none;
  background: transparent;
  padding: 0 !important;
}

.detailed-results-title.is-compact {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--ui-text-3, #909399);
  margin-bottom: 8px !important;
}

.funnel-summary-container {
  display: flex;
  flex-wrap: nowrap;
  overflow-x: auto;
  border-top: 1px solid #e5e7eb;
  gap: 16px;
  padding-bottom: 20px;
  margin-left: 48px;
}

.funnel-summary-step {
  flex: 1;
  min-width: 250px;
  padding: 10px 12px;
  position: relative;
}

.funnel-summary-step:not(:last-child)::after {
  content: '';
  position: absolute;
  right: -8px;
  top: 20px;
  bottom: 20px;
  width: 1px;
  background-color: #e5e7eb;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.step-badge {
  background-color: #f3f4f6;
  color: #374151;
  font-size: 12px;
  font-weight: bold;
  padding: 4px 10px;
  border-radius: 6px;
}

.step-badge-mini {
  background-color: #efefef;
  color: #666;
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
}

.step-title {
  font-size: 14px;
  font-weight: 700;
  color: #111827;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-line {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  margin-bottom: 8px;
  color: #374151;
  font-weight: 500;
}

.stat-line.converted  {
  color: #10b981; /* Green */
}

.stat-line.dropped  {
  color: #ef4444; /* Red */
}

.stat-line span span {
  color: #6b7280;
  margin-left: 6px;
  font-weight: 400;
}

/* Detailed Table Styles */
.detailed-table {
  width: 100%;
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
}

.detailed-table th, .detailed-table td {
  padding: 12px 10px;
  border-bottom: 1px solid #f0f0f0;
}

.breakdown-col-header, .group-header-cell {
  background-color: #f9fafb !important;
  border-bottom: 1px solid #f0f0f0 !important;
  vertical-align: middle;
}

.breakdown-col-header {
  width: 200px;
}

.group-header-cell {
  font-weight: 700;
  color: #374151;
  font-size: 10px;
  white-space: nowrap;
}

.header-group th {
  padding-top: 15px;
  padding-bottom: 5px;
  font-size: 11px;
  color: #909399;
}

.header-cols th {
  background-color: #f9fafb;
  font-weight: 700;
  color: #374151;
  font-size: 10px;
  border-top: 1px solid #f0f0f0;
  white-space: nowrap;
}

.baseline-row {
  background-color: #fcfcfc;
}

.detailed-table tbody tr:hover td,
.detailed-table tbody tr:hover .breakdown-cell {
  background-color: #f0f7ff !important;
}

/* Column color coding */
.cell-converted, .th-converted { color: #10b981; }
.cell-dropped,   .th-dropped   { color: #ef4444; }
.cell-conv-sofar,.th-conv-sofar { color: #3b82f6; }
.cell-conv-prev, .th-conv-prev  { color: #8b5cf6; }

.cell-converted, .cell-dropped {
  font-weight: 500;
}

.breakdown-cell {
  position: sticky;
  left: 0;
  background: white;
  z-index: 1;
  border-right: 1px solid #f0f0f0;
  min-width: 200px;
}

.baseline-row .breakdown-cell {
  background-color: #fcfcfc;
}

.color-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  display: inline-block;
}

.color-dot.baseline {
  border: 1.5px solid #2dd4bf;
  background: white;
}

.step-header-cell {
  border-left: 1px solid #f0f0f0 !important;
}

.funnel-visibility-btn.el-checkbox-button {
  margin-bottom: 0;
  display: inline-flex !important;
}
.funnel-visibility-btn :deep(.el-checkbox-button__inner) {
  width: 16px;
  height: 16px;
  padding: 0 !important;
  display: flex !important;
  align-items: center;
  justify-content: center;
  border-radius: 3px !important;
  border: 1px solid #dcdfe6 !important;
  box-shadow: none !important;
  font-size: 8px;
  line-height: 1;
  background-color: #fff !important;
  color: #c0c4cc !important;
  transition: background-color 0.15s, border-color 0.15s, color 0.15s;
}
.funnel-visibility-btn.is-checked :deep(.el-checkbox-button__inner) {
  background-color: #409EFF !important;
  border-color: #409EFF !important;
  color: #fff !important;
  box-shadow: none !important;
}

.series-color-indicator {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: block;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: transform 0.2s;
}

.series-color-indicator.cursor-pointer:hover {
  transform: scale(1.1);
}

.series-color-indicator.baseline-indicator {
  border: 2px solid #fff;
  outline: 1px solid #2dd4bf;
}

.color-palette-grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  padding: 4px;
}

.color-option {
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  border: 2px solid transparent;
  transition: all 0.2s;
}

.color-option:hover {
  transform: scale(1.1);
}

.color-option.active {
  border-color: #303133;
  box-shadow: 0 0 4px rgba(0,0,0,0.2);
}

.btn-utm-expand {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  margin-right: 6px;
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  color: #909399;
  background: #f5f7fa;
  border: 1px solid #e4e7ed;
  user-select: none;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
}

.btn-utm-expand:hover {
  color: #409EFF;
  background: rgba(64, 158, 255, 0.08);
  border-color: rgba(64, 158, 255, 0.3);
}

.utm-leaf-spacer-inline {
  display: inline-block;
  width: 16px;
  margin-right: 8px;
  flex-shrink: 0;
}

.step-users-link {
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 3px;
}

.step-users-link:hover {
  opacity: 0.75;
}

.step-users-table {
  font-size: 13px;
}

.step-users-table thead th {
  font-size: 11px;
  font-weight: 700;
  color: #374151;
  background-color: #f9fafb;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  white-space: nowrap;
}

.step-user-name-cell {
  max-width: 220px;
}

.step-user-fullname {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 210px;
}

.step-user-username {
  font-size: 11px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 210px;
}
</style>